import { useMsal } from '@azure/msal-react';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { ProjectUserInfo } from '../models/ProjectUserInfo';
import type { ProjektItem } from '../models/ProjektItem';
import type { Projektrolle } from '../models/ProjektRolle';
import { DataService } from '../services/dataService';
import useConfigStore from '../store/config';

interface ProjectContextType {
    project: ProjektItem | null;
    isLoading: boolean;
    error: string | null;
    activeRole: Projektrolle | null;
    userInfo: ProjectUserInfo | null;
    refreshProject?: () => Promise<void>;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { id } = useParams<{ id: string }>();
    const { instance, accounts } = useMsal();
    const { userInfo } = useConfigStore((state) => state.userConfig);

    const [project, setProject] = useState<ProjektItem | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeRole, setActiveRole] = useState<Projektrolle | null>(null);

    const dataService = React.useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    const computeActiveRole = useCallback((proj: ProjektItem) => {
        if (!userInfo || !proj.projektmitarbeiter) {
            return null;
        }

        const activeMember = proj.projektmitarbeiter.find(
            (member) => member.personalID === userInfo.userId && member.isAktiv
        );

        return activeMember ? activeMember.rolle : null;
    }, [userInfo]);

    const refreshProject = async () => {
        if (!dataService || !id) {
            return;
        }

        try {
            const _project = await dataService.getProjekt(parseInt(id));
            if (_project) {
                console.log('Refreshed project details:', _project);
                setProject(_project);
                const role = computeActiveRole(_project);
                setActiveRole(role);
                setError(null);
            }
        } catch (err) {
            console.error('Error refreshing project details:', err);
            setError('Failed to refresh project details');
        }
    };

    useEffect(() => {
        const fetchProjectDetails = async () => {
            if (!dataService || !id) {
                setError('Unable to load project details');
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                const _project = await dataService.getProjekt(parseInt(id));
                if (_project) {
                    console.log('Fetched project details:', _project);
                    setProject(_project);
                    setError(null);
                } else {
                    // Project not found, but don't block with error - show empty state
                    console.warn('Project not found for id:', id);
                    setError(null);
                }
            } catch (err) {
                console.error('Error loading project details:', err);
                // Log the error but don't block - allow page to load with User role
                console.warn('Failed to fetch project details, continuing with limited data. User role should allow basic access.');
                setError(null); // Clear error to allow page to render
            } finally {
                setIsLoading(false);
            }
        };

        fetchProjectDetails();
    }, [dataService, id]);

    useEffect(() => {
        if (project && userInfo) {
            const role = computeActiveRole(project);
            setActiveRole(role);
        }
    }, [project, userInfo, computeActiveRole]);

    return (
        <ProjectContext.Provider value={{ project, isLoading, error, activeRole, userInfo, refreshProject }}>
            {children}
        </ProjectContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useProject = () => {
    const context = useContext(ProjectContext);
    if (context === undefined) {
        throw new Error('useProject must be used within a ProjectProvider');
    }
    return context;
};
